import { useQueryClient } from '@tanstack/react-query';
import { Archive, Download, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { slugify, type AgentRuntime, type ProjectDTO } from '@harness/shared';
import { Modal, PageHeader, Spinner } from '../../components/ui';
import { api, download, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useEnvironment, type ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';

const COLOURS = [null, '#4263eb', '#ec3013', '#1f5c46', '#b7791f', '#7048e8', '#0c8599', '#c2255c'];

export default function GeneralPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const environment = useEnvironment(project.id).data?.environment;
  const hosted = !!environment;
  const init = () => ({
    name: project.name,
    slug: project.slug,
    description: project.description,
    tags: project.tags.join(', '),
    color: project.color,
    branchTemplate: project.branchTemplate,
    agentRuntime: project.agentRuntime,
  });
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => setF(init()), [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(f) !== JSON.stringify(init());

  const refresh = async (p: ProjectDTO) => {
    await qc.invalidateQueries({ queryKey: qk.projects });
    await qc.invalidateQueries({ queryKey: qk.project(p.id) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const p = await api.patch<ProjectDTO>(`/api/projects/${project.id}`, {
        name: f.name.trim(),
        slug: f.slug !== project.slug ? f.slug : undefined,
        description: f.description,
        tags: f.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        color: f.color,
        branchTemplate: f.branchTemplate,
        agentRuntime: f.agentRuntime,
      });
      await refresh(p);
      toast('Project settings saved');
      if (p.slug !== project.slug) nav(`/p/${p.slug}/settings/general`, { replace: true });
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    try {
      const p = await api.patch<ProjectDTO>(`/api/projects/${project.id}`, { archived: !project.archived });
      await refresh(p);
      toast(p.archived ? `${p.name} archived · hidden from the project list` : `${p.name} restored`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const remove = async () => {
    try {
      await api.del(`/api/projects/${project.id}`, { confirm: typed });
      await qc.invalidateQueries({ queryKey: qk.projects });
      toast(`${project.name} deleted`);
      nav('/');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <div className="settings-content narrow">
      <PageHeader title="General" sub="Name, address and defaults for this project." />
      <div className="card card-pad stack">
        <div className="field">
          <label htmlFor="g-name">Name</label>
          <input id="g-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="g-slug">Slug</label>
          <input id="g-slug" className="input mono" value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} />
          <div className="hint">
            {window.location.host}/p/<b style={{ color: 'var(--color-text)' }}>{f.slug}</b>
            {f.slug !== project.slug && <span className="err"> · Changing the slug changes this project’s URLs; old bookmarks stop working.</span>}
          </div>
        </div>
        <div className="field">
          <label htmlFor="g-desc">Description</label>
          <textarea id="g-desc" className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What the project is, in one line" />
        </div>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="g-tags">Tags</label>
            <input id="g-tags" className="input" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="comma separated" />
          </div>
          <div className="field">
            <span className="label">Icon colour</span>
            <div className="row" style={{ minHeight: 36 }}>
              {COLOURS.map((c) => (
                <button
                  key={c ?? 'ink'}
                  type="button"
                  aria-label={c ?? 'Ink (default)'}
                  aria-pressed={f.color === c}
                  onClick={() => setF({ ...f, color: c })}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: c ?? 'var(--tile-lavender-ink)',
                    border: 0,
                    cursor: 'pointer',
                    outline: f.color === c ? '2px solid var(--color-accent)' : 'none',
                    outlineOffset: 2,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="field">
          <label htmlFor="g-branch">Feature branch template</label>
          <input id="g-branch" className="input mono" value={f.branchTemplate} onChange={(e) => setF({ ...f, branchTemplate: e.target.value })} />
          <div className="hint">Variables: {'{issueKey}'} {'{slug}'} — e.g. feature/PROJ-123-add-csv-export</div>
        </div>
        <div className="field">
          <label htmlFor="g-agent-runtime">AI workflow</label>
          <select id="g-agent-runtime" className="input" value={f.agentRuntime} onChange={(e) => setF({ ...f, agentRuntime: e.target.value as AgentRuntime })}>
            <option value="native">None (built-in harness)</option>
            <option value="pi">Pi</option>
            <option value="opencode">OpenCode</option>
          </select>
          <div className="hint">Pi and OpenCode run in this project’s workspace and use the selected model’s provider key there. npm is required; Pi installs Node 22 if needed. Per-tool “Ask” approvals do not use harness approval cards.</div>
        </div>
        <div className="row">
          <button className="btn btn-primary" onClick={save} disabled={!dirty || busy || !f.name.trim()}>
            {busy ? <Spinner /> : null}Save changes
          </button>
          {dirty && (
            <button className="btn btn-ghost" onClick={() => setF(init())}>
              Discard changes
            </button>
          )}
        </div>
      </div>

      <div className="card card-pad">
        <div className="section-title">Danger zone</div>
        <div className="danger-row">
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{project.archived ? 'Restore project' : 'Archive project'}</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {project.archived ? 'Show it in the project list again.' : 'Hide it from the project list without deleting anything.'}
            </div>
          </div>
          <button className="btn btn-secondary" onClick={archive}>
            <Archive className="icon" />
            {project.archived ? 'Restore' : 'Archive'}
          </button>
        </div>
        <div className="danger-row">
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Export project</div>
            <div className="muted" style={{ fontSize: 13 }}>
              A JSON bundle of config, agents, conversations and memory. Secrets are never included.
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => download(`/api/projects/${project.id}/export`)}>
            <Download className="icon" />
            Export JSON
          </button>
        </div>
        <div className="danger-row">
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Delete project</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {hosted ? environment?.provisioningMode === 'shared' ? 'Stops this app’s database login and Daytona sandbox. The shared Supabase project stays online. You can undo deletion for seven days in Hosting settings.' : 'Pauses the Supabase project and Daytona sandbox. You can undo deletion for seven days in Hosting settings.' : 'Deletes agents, conversations, memory and the server link. Files on the server are left alone.'}
            </div>
          </div>
          <button className="btn btn-secondary btn-danger" onClick={() => (setTyped(''), setConfirmDelete(true))}>
            <Trash className="icon" />
            Delete
          </button>
        </div>
      </div>

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} label="Delete project">
        <div className="dialog-title">Delete {project.name}?</div>
        <div className="dialog-body">{hosted ? `This schedules ${environment?.provisioningMode === 'shared' ? 'this app’s database schema and Daytona sandbox' : 'the Supabase project and Daytona sandbox'} for deletion in seven days. You can undo it in Hosting settings during that time. Push any workspace code you want to keep to GitHub before deleting. Type the project name to confirm.` : 'This can’t be undone. If this project uses Daytona, its workspace and files will also be deleted. Type the project name to confirm.'}</div>
        <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={project.name} aria-label="Project name" autoFocus />
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={typed !== project.name} onClick={remove}>
            Delete project
          </button>
        </div>
      </Modal>
    </div>
  );
}
